/// <amd-module name="TFS/Security/Common/SecurityPanel" />
import { createPermissionReport, getSecurityViewData } from "DataSource/SecurityRootViewDataSource";
import * as React from "react";
import { Feature, PerformanceScenarios, ResourceSecurityComponent } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { PermissionsContext } from "TFS/Admin/Common/Contracts/Permissions";
import { createMarkup } from "TFS/Admin/Common/Util/HtmlString";
import { sendToast } from "TFS/Admin/Common/Util/SendToastUtility";
import { AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import * as Constants from "TFS/Security/Common/Constants";
import { IMessageCardContent, ISecurityViewData } from "TFS/Security/Common/Contracts";
import * as Resources from "TFS/Security/Common/Resources";
import { ISecurityPanelProps } from "TFS/Security/Common/Security.Controls.Props";
import { logTelemetry } from "Util";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Button } from "VSSUI/Button";
import { Callout, ContentSize } from "VSSUI/Callout";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Location } from "VSSUI/Utilities/Position";


export class SecurityPanel extends VssComponent<ISecurityPanelProps> {
    private _permissionsContext: PermissionsContext | undefined = undefined;
    private data: ISecurityViewData | undefined;
    private _initialLoadComplete: IObservableValue<boolean> = new ObservableValue<boolean>(false);

    private _calloutMessageCard: IObservableValue<IMessageCardContent | undefined> = new ObservableValue<IMessageCardContent | undefined>(undefined);
    private _calloutAnchorEl: IObservableValue<HTMLDivElement | null> = new ObservableValue<HTMLDivElement | null>(null);
    private _performanceService: IVssPerformanceService;
    private _user: IdentityRef | undefined = undefined;
    private _isExportPermissionsDisabled: IObservableValue<boolean> = new ObservableValue<boolean>(false);

    constructor(props: ISecurityPanelProps, context: IVssComponentContext) {
        super(props, context);

        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        if (vssPageData) {
            this._user = vssPageData.user;
        }

        this._performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._getSecurityViewData();
    }

    public render(): JSX.Element {
        const anchorOrigin = { horizontal: Location.center, vertical: Location.end };
        const calloutOrigin = { horizontal: Location.center, vertical: Location.end };
        const anchorOffset = { horizontal: 0, vertical: 5 };

        return (
            <Panel
                onDismiss={this.props.onCancel}
                className="security-panel"
                size={ContentSize.Auto}
                titleProps={{
                    text: this.props.securityViewTitle,
                    className: "security-title title-m flex-grow scroll-hidden"
                }}
            >
                <div ref={this._saveElement} className="security-panel-view flex-column flex-grow">
                    <div className="security-title flex justify-end">
                        <Observer initialLoadComplete={this._initialLoadComplete} isExportPermissionsDisabled={this._isExportPermissionsDisabled}>
                            {(props: { initialLoadComplete: boolean; isExportPermissionsDisabled: boolean }) =>
                                props.initialLoadComplete &&
                                this._canDownloadReport() && (
                                    <Button className="report-btn" onClick={this._onDownloadReportClick} disabled={props.isExportPermissionsDisabled}>
                                        {Resources.DownloadReportBtn}
                                    </Button>
                                )
                            }
                        </Observer>
                    </div>

                    <Observer initialLoadComplete={this._initialLoadComplete}>
                        {(props: { initialLoadComplete: boolean }) =>
                            props.initialLoadComplete ? (
                                this.data && this.data.userHasReadAccess && this._permissionsContext ? (
                                    <WrappedComponent
                                        dependencies={["ms.vss-admin-web.security-members-common-content"]}
                                        wrappedType="ms.vss-admin-web.security-members-common"
                                        permissionsContext={this._permissionsContext}
                                        onShowMessageCallout={this._showToastMessageCard}
                                        onLogTelemetry={this._logTelemetry}
                                        performanceService={this._performanceService}
                                    />
                                ) : (
                                    <MessageCard className="message-card" severity={MessageCardSeverity.Error}>
                                        <span dangerouslySetInnerHTML={createMarkup(Resources.InvalidPermissionToViewObject)} />
                                    </MessageCard>
                                )
                            ) : (
                                <span>
                                    <Spinner size={SpinnerSize.large} className="spinner" />
                                </span>
                            )
                        }
                    </Observer>
                </div>

                <Observer calloutMessageCard={this._calloutMessageCard} anchorEl={this._calloutAnchorEl}>
                    {(props: { calloutMessageCard: IMessageCardContent | undefined; anchorEl: HTMLDivElement | null }) =>
                        props.calloutMessageCard !== undefined &&
                        props.anchorEl && (
                            <Callout
                                anchorOrigin={anchorOrigin}
                                anchorElement={props.anchorEl!}
                                anchorOffset={anchorOffset}
                                blurDismiss={false}
                                calloutOrigin={calloutOrigin}
                                contentClassName="callout-message-card"
                                contentShadow={true}
                                focuszoneProps={{ focusOnMount: true, includeDefaults: true }}
                                role="alert"
                            >
                                <MessageCard severity={props.calloutMessageCard.severity} onDismiss={this._onDismissToast}>
                                    <span dangerouslySetInnerHTML={createMarkup(props.calloutMessageCard.message)} />
                                </MessageCard>
                            </Callout>
                        )
                    }
                </Observer>
            </Panel>
        );
    }

    private _onDownloadReportClick = (): void => {
        this._createPermissionReport(this.props.resourceId!, this.props.resourceType, this.props.resourceName);
    };

    private _canDownloadReport(): boolean {
        return !!(
            this.props.resourceId &&
            this.props.resourceType &&
            this.data &&
            this.data.userIsCollectionAdmin &&
            this._user &&
            this._user.descriptor &&
            isFeatureFlagEnabled(this.context.pageContext, Constants.EnableExportPermissionsPanelFF, false)
        );
    }

    private _createPermissionReport = (resourceId: string, resourceType: any, resourceName?: string) => {
        const telemetryLogger = logTelemetry(
            this.context.pageContext,
            this.props.permissionSetId,
            ResourceSecurityComponent.SecurityPanel,
            "Export permissions button clicked.",
            {
                user: this._user!.descriptor,
                resourceId: this.props.resourceId
            }
        );

        const reportName: string = this._getReportName();
        this._isExportPermissionsDisabled.value = true;

        createPermissionReport(this.context.pageContext, [], resourceId, resourceType, reportName, resourceName).then(
            async response => {
                sendToast(Resources.DownloadReportToast, this.context.pageContext);
                this._isExportPermissionsDisabled.value = false;
            },
            reason => {
                this._showToastMessageCard(MessageCardSeverity.Error, reason.message || String(reason));
                telemetryLogger.captureError(reason.message || String(reason));
                this._isExportPermissionsDisabled.value = false;
            }
        );
    };

    private _getReportName(): string {
        return `${this._user!.displayName.replace(/\s+/g, "").toLocaleLowerCase()}-${new Date().getTime()}`;
    }

    private _saveElement = (element: HTMLDivElement | null) => {
        this._calloutAnchorEl.value = element;
    };

    private _getSecurityViewData = async () => {
        const telemetryLogger = logTelemetry(
            this.context.pageContext,
            this.props.permissionSetId,
            ResourceSecurityComponent.SecurityPanel,
            "Loading Security Data Model for Initialization."
        );
        this._performanceService.startScenario(PerformanceScenarios.LoadSecurityModel, false);
        this.data = await getSecurityViewData(this.context.pageContext, this.props.permissionSetId, this.props.permissionSetToken);

        if (this.data) {
            // With Json it's unclear whether it's type is undefined or the it actually contains the string undefined
            if (typeof this.data.permissionsContextJson !== "undefined" && this.data.permissionsContextJson !== "undefined") {
                const permissionsContext: PermissionsContext = JSON.parse(this.data.permissionsContextJson) as PermissionsContext;
                this._permissionsContext = permissionsContext;
                this._permissionsContext.allowAADSearchInHosted = this.props.allowAADSearchInHosted || false;
            } else {
                this.data = undefined;
                telemetryLogger.captureError("getSecurityViewData: Undefined permissionsContextJson");
            }
        } else {
            telemetryLogger.captureError("getSecurityViewData: Data provider load failed. Check product trace.");
        }

        this._performanceService.endScenario(Feature.ResourceSecurity, PerformanceScenarios.LoadSecurityModel, false);
        this._initialLoadComplete.value = true;
    };

    private _showToastMessageCard = (severity: MessageCardSeverity, message: string) => {
        this._calloutMessageCard.value = { severity: severity, message: message } as IMessageCardContent;
    };

    private _onDismissToast = () => {
        this._calloutMessageCard.value = undefined;
    };

    private _logTelemetry = (
        component: ResourceSecurityComponent,
        action: string,
        properties?: { [key: string]: any }
    ): AdminFeatureTelemetryLogger => {
        return logTelemetry(this.context.pageContext, this.props.permissionSetId, component, action, properties);
    };
}

VssComponent.register("ms.vss-admin-web.security-panel-component", SecurityPanel);
